import React, { useMemo } from 'react';
import { Box, Autocomplete, TextField, Stack, Paper, Button, InputAdornment } from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PhoneIcon from '@mui/icons-material/Phone';
import CategoryIcon from '@mui/icons-material/Category';
import SearchIcon from '@mui/icons-material/Search';
import { 
  getUniqueCustomerLocations, 
  getUniqueCategories, 
  getUniqueCustomerNumbers 
} from '../../utils/filterUtils';
import { displayMobileNumber } from '../../utils/formatDate';

const CustomerFilter = ({ filters, onChange, customers = [], invoices = [] }) => {
  const handleChange = (field, value) => {
    onChange({ ...filters, [field]: value });
  };

  const categories = useMemo(() => getUniqueCategories([], invoices), [invoices]);
  const locations = useMemo(() => getUniqueCustomerLocations(customers), [customers]);
  const customerNumbers = useMemo(() => getUniqueCustomerNumbers(customers), [customers]);

  return (
    <Paper sx={{ p: 3, border: '1px solid', borderColor: 'divider', borderRadius: 2, bgcolor: 'background.paper', mb: 2 }} elevation={0}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
        <Autocomplete
          options={locations}
          value={filters.location || ''}
          onChange={(_, v) => handleChange('location', v)}
          sx={{ minWidth: 160 }}
          renderInput={params => (
            <TextField
              {...params}
              label="Location"
              variant="outlined"
              size="small"
              sx={{
                '& .MuiInputBase-input': {
                  textOverflow: 'ellipsis',
                  overflow: 'hidden',
                  whiteSpace: 'nowrap'
                }
              }}
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <InputAdornment position="start">
                    <LocationOnIcon color="primary" />
                  </InputAdornment>
                ),
              }}
            />
          )}
        />
        <Autocomplete
          options={customerNumbers}
          value={filters.customerNumber || ''}
          onChange={(_, v) => handleChange('customerNumber', v)}
          getOptionLabel={(option) => displayMobileNumber(option)}
          sx={{ minWidth: 160 }}
          renderInput={params => (
            <TextField
              {...params}
              label="Customer Number"
              variant="outlined"
              size="small"
              sx={{
                '& .MuiInputBase-input': {
                  textOverflow: 'ellipsis',
                  overflow: 'hidden',
                  whiteSpace: 'nowrap'
                }
              }}
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <InputAdornment position="start">
                    <PhoneIcon color="primary" />
                  </InputAdornment>
                ),
              }}
            />
          )}
        />
        <Autocomplete
          options={categories}
          value={filters.category || ''}
          onChange={(_, v) => handleChange('category', v)}
          sx={{ minWidth: 160 }}
          renderInput={params => (
            <TextField
              {...params}
              label="Category"
              variant="outlined"
              size="small"
              sx={{
                '& .MuiInputBase-input': {
                  textOverflow: 'ellipsis',
                  overflow: 'hidden',
                  whiteSpace: 'nowrap'
                }
              }}
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <InputAdornment position="start">
                    <CategoryIcon color="primary" />
                  </InputAdornment>
                ),
              }}
            />
          )}
        />
        <TextField
          label="Purchase Date"
          type="date"
          size="small"
          InputLabelProps={{ shrink: true }}
          value={filters.purchaseDate || ''}
          onChange={e => handleChange('purchaseDate', e.target.value)}
          sx={{ minWidth: 150 }}
        />
        <Button variant="contained" color="primary" startIcon={<SearchIcon />} sx={{ height: 40, minWidth: 100 }}>
          Search
        </Button>
        <Button variant="outlined" color="primary" onClick={() => onChange({})} sx={{ height: 40, minWidth: 100 }}>
          Clear Filters
        </Button>
      </Stack>
    </Paper>
  );
};

export default CustomerFilter; 
